import { FC, useState } from 'react';
import { Box, Typography, Avatar, IconButton } from '@mui/material';

import styles from './TravelBuddyCard.module.scss';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { TripCreator, TripTravelBuddy } from '../../../../../../types/trip.types';
import { EmailIcon, PhoneIcon, DeleteIcon } from '../../../../../../components/Icons';
import ThreeDotsMenu, { MenuItemConfig } from '../../../../../../components/ThreeDotsMenu/ThreeDotsMenu';
import EmergencyContacts from '../EmergencyContacts/EmergencyContacts';
import { buildAvatarUrl, generateInitialsFromFullName } from '../../../../../../utils/avatarUtils';

interface TravelBuddyCardProps {
  buddy: TripTravelBuddy | TripCreator;
  isCreator?: boolean;
  onRemove?: (buddyId: number) => void;
}

const TravelBuddyCard: FC<TravelBuddyCardProps> = ({ buddy, isCreator = false, onRemove }) => {
  const [showEmergencyContacts, setShowEmergencyContacts] = useState(false);

  const buddyMenuItems: MenuItemConfig[] = [
    ...(onRemove && !isCreator
      ? [
          {
            id: 'remove',
            label: 'Remove from Trip',
            icon: <DeleteIcon />,
            variant: 'danger' as const,
            onClick: () => {
              onRemove(buddy.id);
            },
          },
        ]
      : []),
  ];

  const getBuddyDisplayName = () => {
    if (buddy.full_name) {
      return buddy.full_name;
    }
    return buddy.email || 'Unknown User';
  };

  const getBuddyAvatarText = () => {
    return generateInitialsFromFullName(buddy.full_name, buddy.email);
  };

  const getBuddyAvatarUrl = () => {
    return buildAvatarUrl(buddy.profile_picture);
  };

  const getRelationshipText = () => {
    if (isCreator) return 'Creator';

    const travelBuddy = buddy as TripTravelBuddy;

    const relationship = travelBuddy.relationship;
    return relationship ? relationship.charAt(0).toUpperCase() + relationship.slice(1) : 'Friend';
  };

  const shouldShowEmergencyButton = () => {
    if (isCreator) return true;

    const travelBuddy = buddy as TripTravelBuddy;
    return !!travelBuddy.relationship;
  };

  const toggleEmergencyContacts = () => {
    setShowEmergencyContacts(!showEmergencyContacts);
  };

  return (
    <Box className={styles.travelBuddyCard}>
      <Box className={styles.buddyMainInfo}>
        <Avatar
          src={getBuddyAvatarUrl() || undefined}
          className={styles.buddyAvatar}
          sx={{
            width: 90,
            height: 90,
            backgroundColor: getBuddyAvatarUrl() ? 'transparent' : '#e3f2fd',
            color: getBuddyAvatarUrl() ? 'transparent' : '#0098f0',
            fontSize: '24px',
            fontWeight: 600,
          }}
        >
          {!getBuddyAvatarUrl() && getBuddyAvatarText()}
        </Avatar>

        <Box className={styles.buddyDetails}>
          <Box className={styles.buddyHeader}>
            <Typography
              variant="h9Semibold"
              className={styles.buddyName}
            >
              {getBuddyDisplayName()}
            </Typography>
            <Typography
              variant="h11"
              className={styles.buddyRelationship}
            >
              • {getRelationshipText()}
            </Typography>
            {isCreator && (
              <Box className={styles.creatorChip}>
                <Typography variant="h12Semibold">Creator</Typography>
              </Box>
            )}
          </Box>

          <Box className={styles.buddyInfo}>
            {buddy.email && (
              <Box className={styles.contactInfo}>
                <EmailIcon
                  size={24}
                  color="#5E6A81"
                />
                <Typography variant="h11">{buddy.email}</Typography>
              </Box>
            )}

            {buddy.phone_number && (
              <Box className={styles.phoneRow}>
                <Box className={styles.contactInfo}>
                  <PhoneIcon
                    size={24}
                    color="#5E6A81"
                  />
                  <Typography variant="h11">{buddy.phone_number}</Typography>
                </Box>

                {shouldShowEmergencyButton() && (
                  <Box
                    className={styles.emergencyContactsButton}
                    onClick={toggleEmergencyContacts}
                  >
                    <Typography
                      variant="h11"
                      className={styles.emergencyContactsText}
                    >
                      {showEmergencyContacts ? 'Hide emergency contacts' : 'Show emergency contacts'}
                    </Typography>
                    <IconButton
                      className={styles.expandButton}
                      size="small"
                    >
                      {showEmergencyContacts ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                    </IconButton>
                  </Box>
                )}
              </Box>
            )}

            {!buddy.phone_number && shouldShowEmergencyButton() && (
              <Box
                className={styles.emergencyContactsButtonStandalone}
                onClick={toggleEmergencyContacts}
              >
                <Typography
                  variant="h11"
                  className={styles.emergencyContactsText}
                >
                  {showEmergencyContacts ? 'Hide emergency contacts' : 'Show emergency contacts'}
                </Typography>
                <IconButton
                  className={styles.expandButton}
                  size="small"
                >
                  {showEmergencyContacts ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                </IconButton>
              </Box>
            )}
          </Box>
        </Box>

        {buddyMenuItems.length > 0 && (
          <ThreeDotsMenu
            menuItems={buddyMenuItems}
            iconSize={28}
            className={styles.buddyMenuButton}
          />
        )}
      </Box>

      {shouldShowEmergencyButton() && (
        <EmergencyContacts
          contacts={buddy.emergency_contacts || []}
          isExpanded={showEmergencyContacts}
        />
      )}
    </Box>
  );
};

export default TravelBuddyCard;
